import { ArrowUpRight, ChevronDown, X, Search, ChevronsUpDown, ArrowUpDown, Star } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { setShowBackToDashboardButton, setSelectedSender, updateTabSelectedSender, updateWorkflowSessionState } from '../../store/reducers';
import { selectTabById, selectActiveTabData } from '../../store/selectors/tabSelectors';
import { useState, useEffect, useMemo } from 'react';
import { useLanguage, getReviewSenderDetailsTranslation } from '../../hooks/useLanguage';
import AiIcon from '../../assets/ai-icon.png';
import { extractSuggestionsFromData } from '../../utils/suggestionUtils';

export const ReviewSenderDetails = ({ contacts, navigate, tabId, declarationIndex = 0 }) => {
    const dispatch = useDispatch();
    
    // CRITICAL: Get active workflow session (source of truth for storage)
    const activeWorkflowSessionId = useSelector(state => state.workflowSessions?.activeSessionId);
    const activeWorkflowSession = useSelector(state => 
        activeWorkflowSessionId ? state.workflowSessions?.sessions?.[activeWorkflowSessionId] : null
    );
    
    // Get selectedSender from workflow session arrays (all declarations together, NOT split by tabs)
    const workflowSelectedSenders = activeWorkflowSession?.state?.selectedSender || [];
    const workflowSelectedSender = workflowSelectedSenders[declarationIndex] || null;
    
    // Fallback to tab-based or global for backward compatibility (UI only)
    const tabData = useSelector(state => tabId ? selectTabById(state, tabId) : selectActiveTabData(state));
    const tabSelectedSender = tabData ? tabData.selectedSender : null;
    
    // Fallback to global array-based for backward compatibility
    const globalSelectedSenders = useSelector(state => state.user.selectedSender || []);
    const globalSelectedSender = globalSelectedSenders[declarationIndex] || null;
    
    // Priority: workflow session > tab > global
    const finalSelectedSender = workflowSelectedSender || tabSelectedSender || globalSelectedSender;
    
    // Get extracted/editable data for suggestions (from workflow session, all declarations together)
    const workflowEditableData = activeWorkflowSession?.state?.editableData || null;
    const extractedData = useSelector(state => state.documents.extractedData);
    const editableDataFromRedux = useSelector(state => state.documents.editableData);
    // Priority: workflow session > tab > global
    const editableData = workflowEditableData || (tabData ? tabData.editableData : editableDataFromRedux);
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('name-asc');
    const [isSortOpen, setIsSortOpen] = useState(false);
    const [isSpanish] = useLanguage();

    // Extract suggestions from data arrays
    const dataForSuggestions = editableData || extractedData;
    const declarationIdx = tabData ? tabData.declarationIndex : declarationIndex;
    const suggestions = useMemo(() => {
      return extractSuggestionsFromData(
        Array.isArray(dataForSuggestions) ? dataForSuggestions : [],
        declarationIdx
      );
    }, [dataForSuggestions, declarationIdx]);
    
    const suggestedSenders = suggestions.remitentes || [];
    
    // Match suggested senders to contacts by nombre and eori (no id matching)
    const matchedSuggestedSenders = useMemo(() => {
      return suggestedSenders
        .map(suggestion => {
          const matchedContact = contacts.find(contact => {
            // Match by nombre and eori
            const nombreMatch = contact.nombre && suggestion.nombre && 
              contact.nombre.toLowerCase().trim() === suggestion.nombre.toLowerCase().trim();
            const eoriMatch = contact.eori && suggestion.eori && 
              contact.eori.toLowerCase().trim() === suggestion.eori.toLowerCase().trim();
            
            return nombreMatch && eoriMatch;
          });
          return matchedContact ? { ...matchedContact, score: suggestion.score, reason: suggestion.reason } : null;
        })
        .filter(Boolean)
        .sort((a, b) => (b.score || 0) - (a.score || 0)) // Sort by score descending
        .slice(0, 3); // Limit to top 3
    }, [suggestedSenders, contacts]);

    const sortOptions = [
        { value: 'name-asc', label: getReviewSenderDetailsTranslation('nameAZ', isSpanish) },
        { value: 'name-desc', label: getReviewSenderDetailsTranslation('nameZA', isSpanish) },
        { value: 'date-desc', label: getReviewSenderDetailsTranslation('newestFirst', isSpanish) },
        { value: 'date-asc', label: getReviewSenderDetailsTranslation('oldestFirst', isSpanish) }
    ];

    const handleSenderSelect = (sender) => {
        // CRITICAL: Save to workflow session arrays (all declarations together, NOT split by tabs) Workflow sessions are the source of truth: workflow-1.state.selectedSender[] = array
        if (activeWorkflowSessionId) {
            const currentSenders = [...(workflowSelectedSenders || [])];
            // Ensure array is large enough
            while (currentSenders.length <= declarationIndex) {
                currentSenders.push(null);
            }
            currentSenders[declarationIndex] = sender;
            dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
                selectedSender: currentSenders,
            }));
            // CRITICAL: Also sync to global Redux for components that read from there
            dispatch(setSelectedSender(sender, declarationIndex));
        } else {
            // Fallback: Save to global arrays (backward compatibility)
            dispatch(setSelectedSender(sender, declarationIndex));
        }
        setIsModalOpen(false);
        setSearchQuery('');
    };

    // Filter contacts by search query across multiple fields
    const filteredContacts = useMemo(() => {
        if (!searchQuery || typeof searchQuery !== 'string') {
            return contacts;
        }
        
        const query = searchQuery.toLowerCase().trim();
        if (!query) {
            return contacts;
        }
        
        return contacts.filter(contact => {
            if (!contact || typeof contact !== 'object') {
                return false;
            }
            
            // Helper function to safely convert and search a field
            const searchInField = (field) => {
                if (field == null) return false;
                if (typeof field === 'string' && field.trim() === '') return false;
                if (typeof field === 'number') {
                    return String(field).toLowerCase().includes(query);
                }
                const fieldStr = String(field).toLowerCase().trim();
                return fieldStr.length > 0 && fieldStr.includes(query);
            };
            
            // Search across multiple fields
            return searchInField(contact.nombre) ||
                   searchInField(contact.identificacion_fiscal) ||
                   searchInField(contact.nif) ||
                   searchInField(contact.eori) ||
                   searchInField(contact.direccion) ||
                   searchInField(contact.localidad) ||
                   searchInField(contact.pais);
        });
    }, [contacts, searchQuery]);

    // Sort filtered contacts
    const sortedContacts = useMemo(() => {
        return [...filteredContacts].sort((a, b) => {
            switch (sortBy) {
                case 'name-asc':
                    return (a.nombre || '').localeCompare(b.nombre || '');
                case 'name-desc':
                    return (b.nombre || '').localeCompare(a.nombre || '');
                case 'date-asc':
                    return new Date(a.date_created || 0) - new Date(b.date_created || 0);
                case 'date-desc':
                    return new Date(b.date_created || 0) - new Date(a.date_created || 0);
                default:
                    return 0;
            }
        });
    }, [filteredContacts, sortBy]);

    const getSortLabel = () => {
        return sortOptions.find(option => option.value === sortBy)?.label || getReviewSenderDetailsTranslation('sortBy', isSpanish);
    };

    return (
        <div className="text-left text-sm flex flex-col" id="review-sender">
            <div className='flex justify-between gap-4 mb-2 items-center'>
                <h1 className='font-semibold'>{getReviewSenderDetailsTranslation('sender', isSpanish)}</h1>
            </div>
            
            <div className={`p-4 border-container rounded-xl form ${finalSelectedSender ? "flex-1" : ""}`}>
                <div className='relative w-full'>
                    <button className={`w-full flex gap-2 justify-between items-center ${finalSelectedSender ? "border-container rounded-md p-4 " : ""}`} onClick={() => setIsModalOpen(true)}>
                        {finalSelectedSender ? <p className='truncate'>{finalSelectedSender.nombre}</p> : <p className='opacity-75 truncate'>{getReviewSenderDetailsTranslation('selectSender', isSpanish)}</p>}
                        <ChevronsUpDown className="w-4 h-4" />
                    </button>
                </div>
                {finalSelectedSender && (
                    <>
                        <div className='review-flex flex gap-4 mt-4'>
                            <div className="form-group flex-1" id={`sender_nombre${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                                <label htmlFor="companyName">{getReviewSenderDetailsTranslation('senderName', isSpanish)}</label>
                                <p className='font-bold'>{finalSelectedSender && finalSelectedSender.nombre || "N/A"}</p>
                            </div>
                            <div className="form-group flex-1" id={`sender_nif${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                                <label htmlFor="address">{getReviewSenderDetailsTranslation('senderTaxId', isSpanish)}</label>
                                <p className='font-bold'>{finalSelectedSender && finalSelectedSender.nif || "N/A"}</p>
                            </div>
                        </div>
                        <div className='review-flex flex gap-4'>
                            <div className="form-group flex-1" id={`sender_eori${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                                <label htmlFor="taxId">{getReviewSenderDetailsTranslation('senderEori', isSpanish)}</label>
                                <p className='font-bold'>{finalSelectedSender && finalSelectedSender.eori  || "N/A"}</p>
                            </div>
                            <div className="form-group flex-1" id={`sender_direccion${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                                <label htmlFor="eori">{getReviewSenderDetailsTranslation('senderAddress', isSpanish)}</label>
                                <p className='font-bold'>{finalSelectedSender && finalSelectedSender.direccion  || "N/A"}</p>
                            </div>
                        </div>
                        <div className='review-flex flex gap-4'>
                            <div className="form-group flex-1" id={`sender_localidad${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                                <label htmlFor="city">{getReviewSenderDetailsTranslation('senderCity', isSpanish)}</label>
                                <p className='font-bold'>{finalSelectedSender && finalSelectedSender.localidad  || "N/A"}</p>
                            </div>
                            <div className="form-group flex-1" id={`sender_pais${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                                <label htmlFor="postalCode">{getReviewSenderDetailsTranslation('senderCountry', isSpanish)}</label>
                                <p className='font-bold'>{finalSelectedSender && finalSelectedSender.pais  || "N/A"}</p>
                            </div>
                        </div>
                    </>
                )}
            </div>



             {/* Suggested Senders */}
             {!finalSelectedSender && matchedSuggestedSenders.length > 0 && (
                    <>
                    <h2 className="text-xs font-light mt-4">{getReviewSenderDetailsTranslation('suggestedSenders', isSpanish)}</h2>
                <div className="flex flex-wrap gap-2 mt-2">
                    {matchedSuggestedSenders.map((suggestedSender, index) => (
                        <button
                            key={`suggested-sender-${declarationIndex}-${suggestedSender.id || index}-${index}`}
                            onClick={() => handleSenderSelect(suggestedSender)}
                            className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-full transition-colors"
                            title={suggestedSender.reason || `Score: ${(suggestedSender.score * 100).toFixed(0)}%`}
                        >
                          <img src={AiIcon} alt="AI Icon" className="w-4 h-4" />
                            <span className="font-medium text-xs truncate">{suggestedSender.nombre}</span>
                            {suggestedSender.score && (
                                <span className="text-[8px] text-blue-400 font-semibold ">
                                    {(suggestedSender.score * 100).toFixed(0)}%
                                </span>
                              )}
                        </button>
                    ))}
                </div>
                </>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 animate-modalEnter">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">{getReviewSenderDetailsTranslation('selectSender', isSpanish)}</h2>
                            <button onClick={() => {
                                setIsModalOpen(false);
                                setSearchQuery('');
                            }} className="p-1 hover:bg-gray-100 rounded-full">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
       
                        <div className="flex gap-2 mb-4">
                            {/* Search Input */}
                            <div className="relative flex-1">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Search className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    className="w-full pl-10 pr-4 py-2 border-container rounded-full "
                                    placeholder={getReviewSenderDetailsTranslation('searchSenders', isSpanish)}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>

                            {/* Custom Sort Dropdown */}
                            <div className="relative button-no-hover-effect">
                                <button
                                    className="flex items-center gap-2 px-2 pr-0 py-2 rounded-full"
                                    onClick={() => setIsSortOpen(!isSortOpen)}
                                >
                                    <ArrowUpDown className="w-4 h-4" />
                                    <span>{getSortLabel()}</span>
                                    <ChevronDown className={`w-4 h-4 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} />
                                </button>
                                
                                {isSortOpen && (
                                    <div className="overflow-hidden absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border-container py-1 z-10">
                                        {sortOptions.map((option) => (
                                            <button
                                                key={option.value}
                                                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                                                    sortBy === option.value ? 'text-primary' : 'text-gray-700'
                                                }`}
                                                onClick={() => {
                                                    setSortBy(option.value);
                                                    setIsSortOpen(false);
                                                }}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>        

                        <div className="max-h-[60vh] min-h-[60vh] overflow-y-auto">
                            {contacts.length > 0 ? (
                                <div className="space-y-0">
                                    {sortedContacts.length > 0 ? (
                                        sortedContacts.map((contact, index) => (
                                            <div 
                                                key={`sender-${declarationIndex}-${contact.id || index}-${index}`} 
                                                className='p-3 px-0 hover:bg-gray-100 cursor-pointer border-bottom-gray'
                                                onClick={() => handleSenderSelect(contact)}
                                            >
                                                <div className="flex justify-between items-start gap-2">
                                                    <div>
                                                        <p className="font-medium">{contact.nombre}</p>
                                                        <p className="text-sm text-gray-500">
                                                            {contact.pais}
                                                        </p>
                                                    </div>
                                                    {(contact.nif) && (
                                                        <span className="text-xs bg-bgGray px-3 py-1 rounded-full">
                                                            {contact.nif || contact.nif}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-4 text-gray-500">
                                            {getReviewSenderDetailsTranslation('noSendersFound', isSpanish)} "{searchQuery}"
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <p className='opacity-75 mb-4'>{getReviewSenderDetailsTranslation('noSendersAvailable', isSpanish)}</p>
                                    <button 
                                        className='flex gap-2 items-center justify-center mx-auto px-4 py-2 bg-default text-white rounded-full'
                                        onClick={() => {
                                            setIsModalOpen(false);
                                            dispatch(setShowBackToDashboardButton(true));
                                            navigate("/sender-management");
                                        }}
                                    >
                                        <p>{getReviewSenderDetailsTranslation('addSender', isSpanish)}</p>
                                        <ArrowUpRight className='w-5 h-5 stroke-white' />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}